import React from 'react';
import { useParams } from 'wouter';
import { Layout } from '@/components/layout';
import { Button, CompactSelect } from '@/components/ui-elements';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSession,
  listTransactions,
  importTransactions,
  runReconciliation,
  patchTransaction,
  listGatewayMappings,
  upsertGatewayMapping,
  exportTransactionsCsvUrl,
  exportReckonCsvUrl,
  type ReconciliationTransaction,
  type ReconciliationSummary,
} from '@/lib/reconcile-api';
import { SOURCE_PARSERS, SOURCE_LABELS, type SourceType } from '@/lib/csv-parsers';
import { fmtDate } from '@/lib/dateFormat';
import {
  Upload,
  Play,
  Download,
  ChevronLeft,
  X,
  Link2,
  Ban,
  Unlink,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  MinusCircle,
} from 'lucide-react';
import { useLocation } from 'wouter';

const SOURCE_OPTIONS: SourceType[] = [
  'shopify_orders',
  'shopify_payouts',
  'afterpay',
  'zip_pay',
  'paypal',
  'bank_manual',
];

function matchStatusBadge(status: ReconciliationTransaction['matchStatus']) {
  switch (status) {
    case 'matched':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          <CheckCircle2 className="w-3 h-3" /> Matched
        </span>
      );
    case 'discrepancy':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
          <AlertCircle className="w-3 h-3" /> Discrepancy
        </span>
      );
    case 'dismissed':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-slate-500/10 text-muted-foreground border border-slate-500/20">
          <MinusCircle className="w-3 h-3" /> Dismissed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20">
          <HelpCircle className="w-3 h-3" /> Unmatched
        </span>
      );
  }
}

type FileEntry = { file: File; source: SourceType };

interface ManualLinkModalProps {
  txn: ReconciliationTransaction;
  allTxns: ReconciliationTransaction[];
  onLink: (linkedId: string) => void;
  onClose: () => void;
}

function ManualLinkModal({ txn, allTxns, onLink, onClose }: ManualLinkModalProps) {
  const candidates = allTxns.filter(
    (t) => t.id !== txn.id && t.matchStatus === 'unmatched',
  );
  const [selected, setSelected] = React.useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="glass-panel rounded-2xl border border-border w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg">Link Transaction</h3>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Select an unmatched transaction to link with:{' '}
          <span className="text-primary font-mono">
            {txn.orderId || txn.reference || txn.externalId || txn.id.slice(0, 8)}
          </span>{' '}
          (${txn.amount})
        </p>
        <div className="max-h-60 overflow-y-auto space-y-2 mb-4">
          {candidates.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">No unmatched transactions available.</p>
          ) : (
            candidates.map((c) => (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  selected === c.id
                    ? 'border-primary/50 bg-primary/10'
                    : 'border-border hover:border-border hover:bg-muted/50'
                }`}
              >
                <input
                  type="radio"
                  name="link-txn"
                  value={c.id}
                  checked={selected === c.id}
                  onChange={() => setSelected(c.id)}
                  className="sr-only"
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-foreground">
                    {c.orderId || c.reference || c.externalId || c.id.slice(0, 8)}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {c.source} · {c.gateway} ·{' '}
                    {fmtDate(c.transactionDate)}
                  </div>
                </div>
                <span className="font-mono text-sm text-foreground">${c.amount}</span>
              </label>
            ))
          )}
        </div>
        <div className="flex gap-3">
          <Button onClick={() => selected && onLink(selected)} disabled={!selected}>
            <Link2 className="w-4 h-4 mr-2" />
            Link
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ReconciliationDetail() {
  const { id } = useParams<{ id: string }>();
  const token = useAuthStore((s) => s.token);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = React.useState<'transactions' | 'reckon'>('transactions');
  const [files, setFiles] = React.useState<FileEntry[]>([]);
  const [filterStatus, setFilterStatus] = React.useState<string>('all');
  const [filterGateway, setFilterGateway] = React.useState<string>('all');
  const [linkTxn, setLinkTxn] = React.useState<ReconciliationTransaction | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [pendingFileSource, setPendingFileSource] = React.useState<SourceType>('shopify_orders');

  // Reckon account editing state: gateway -> accountName
  const [editedMappings, setEditedMappings] = React.useState<Record<string, string>>({});
  const [savingMapping, setSavingMapping] = React.useState<string | null>(null);

  const sessionKey = ['reconciliation-session', id];
  const txnsKey = ['reconciliation-transactions', id];
  const mappingsKey = ['gateway-mappings'];

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: sessionKey,
    queryFn: () => getSession(token, id!),
    enabled: !!id,
  });

  const { data: transactions, isLoading: txnsLoading } = useQuery({
    queryKey: txnsKey,
    queryFn: () => listTransactions(token, id!),
    enabled: !!id && activeTab === 'transactions',
  });

  const { data: mappings } = useQuery({
    queryKey: mappingsKey,
    queryFn: () => listGatewayMappings(token),
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      let total = 0;
      for (const entry of files) {
        const text = await entry.file.text();
        const parser = SOURCE_PARSERS[entry.source];
        const parsed = parser(text);
        if (parsed.length === 0) continue;
        const result = await importTransactions(token, id!, parsed);
        total += result.imported;
      }
      return total;
    },
    onSuccess: (count) => {
      toast({ title: `Imported ${count} transactions` });
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: txnsKey });
    },
    onError: (err: Error) =>
      toast({ title: 'Import failed', description: err.message, variant: 'destructive' }),
  });

  const runMutation = useMutation({
    mutationFn: () => runReconciliation(token, id!),
    onSuccess: () => {
      toast({ title: 'Reconciliation complete' });
      queryClient.invalidateQueries({ queryKey: sessionKey });
      queryClient.invalidateQueries({ queryKey: txnsKey });
    },
    onError: (err: Error) =>
      toast({ title: 'Run failed', description: err.message, variant: 'destructive' }),
  });

  const patchMutation = useMutation({
    mutationFn: (p: { txnId: string; action: 'link' | 'dismiss' | 'unlink'; linkedTxnId?: string }) =>
      patchTransaction(token, id!, p.txnId, { action: p.action, linkedTxnId: p.linkedTxnId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: txnsKey });
      queryClient.invalidateQueries({ queryKey: sessionKey });
      setLinkTxn(null);
    },
    onError: (err: Error) =>
      toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const handleFileAdd = (fileList: FileList | null) => {
    if (!fileList) return;
    const newEntries: FileEntry[] = Array.from(fileList).map((f) => ({
      file: f,
      source: pendingFileSource,
    }));
    setFiles((prev) => [...prev, ...newEntries]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFileAdd(e.dataTransfer.files);
  };

  const handleSaveMapping = async (gateway: string) => {
    const name = editedMappings[gateway];
    if (!name) return;
    setSavingMapping(gateway);
    try {
      await upsertGatewayMapping(token, { gateway, accountName: name });
      queryClient.invalidateQueries({ queryKey: mappingsKey });
      toast({ title: 'Mapping saved' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
    setSavingMapping(null);
  };

  const summary = session?.summary as ReconciliationSummary | null | undefined;
  const gateways = transactions
    ? [...new Set(transactions.map((t) => t.gateway))]
    : [];

  const filteredTxns = transactions?.filter((t) => {
    const statusOk = filterStatus === 'all' || t.matchStatus === filterStatus;
    const gwOk = filterGateway === 'all' || t.gateway === filterGateway;
    return statusOk && gwOk;
  });

  const getMappedName = (gateway: string) => {
    if (editedMappings[gateway] !== undefined) return editedMappings[gateway];
    return mappings?.find((m) => m.gateway === gateway)?.accountName ?? gateway;
  };

  function downloadExport(url: string, filename: string) {
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      });
  }

  if (sessionLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!session) {
    return (
      <Layout>
        <div className="text-center py-32 text-muted-foreground">Session not found.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => setLocation('/reconciliation')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Sessions
        </button>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display text-gradient mb-1">{session.name}</h1>
            <p className="text-muted-foreground text-sm">
              {fmtDate(session.periodStart)} –{' '}
              {fmtDate(session.periodEnd)}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() =>
                downloadExport(
                  exportTransactionsCsvUrl(id!),
                  `reconciliation-${id}.csv`,
                )
              }
              variant="ghost"
              disabled={session.status !== 'done'}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Reconciled CSV
            </Button>
            <Button
              onClick={() =>
                downloadExport(exportReckonCsvUrl(id!), `reckon-summary-${id}.csv`)
              }
              variant="ghost"
              disabled={session.status !== 'done'}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Reckon Summary
            </Button>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="glass-panel rounded-xl border border-emerald-500/20 p-4">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
              Matched
            </div>
            <div className="text-2xl font-display font-bold text-emerald-400">
              {summary.matched}
            </div>
            <div className="text-xs text-muted-foreground">
              ${Number(summary.matchedAmount || 0).toFixed(2)}
            </div>
          </div>
          <div className="glass-panel rounded-xl border border-rose-500/20 p-4">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
              Unmatched
            </div>
            <div className="text-2xl font-display font-bold text-rose-400">
              {summary.unmatched}
            </div>
            <div className="text-xs text-muted-foreground">
              ${Number(summary.unmatchedAmount || 0).toFixed(2)}
            </div>
          </div>
          <div className="glass-panel rounded-xl border border-amber-500/20 p-4">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
              Discrepancies
            </div>
            <div className="text-2xl font-display font-bold text-amber-400">
              {summary.discrepancies}
            </div>
            <div className="text-xs text-muted-foreground">
              ${Number(summary.discrepancyAmount || 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Import zone */}
      <div className="glass-panel rounded-2xl border border-border p-6 mb-6">
        <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">
          Upload CSV Files
        </h2>

        {/* Source type selector */}
        <div className="flex flex-wrap gap-2 mb-4">
          {SOURCE_OPTIONS.map((src) => (
            <button
              key={src}
              type="button"
              onClick={() => setPendingFileSource(src)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                pendingFileSource === src
                  ? 'bg-primary/20 text-primary border-primary/30'
                  : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {SOURCE_LABELS[src]}
            </button>
          ))}
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragging
              ? 'border-primary/60 bg-primary/5'
              : 'border-border hover:border-border hover:bg-muted/30'
          }`}
        >
          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Drop CSV files here or{' '}
            <span className="text-primary underline underline-offset-2">browse</span>
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Selected type: <strong className="text-muted-foreground">{SOURCE_LABELS[pendingFileSource]}</strong>
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            multiple
            className="hidden"
            onChange={(e) => handleFileAdd(e.target.files)}
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((entry, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border"
              >
                <div className="flex-1 text-sm">
                  <span className="text-foreground">{entry.file.name}</span>
                  <span className="ml-2 text-muted-foreground text-xs">
                    ({SOURCE_LABELS[entry.source]})
                  </span>
                </div>
                <CompactSelect
                  value={entry.source}
                  onChange={(e) =>
                    setFiles((prev) =>
                      prev.map((f, j) =>
                        j === i ? { ...f, source: e.target.value as SourceType } : f,
                      ),
                    )
                  }
                  className="h-8 text-xs"
                >
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {SOURCE_LABELS[s]}
                    </option>
                  ))}
                </CompactSelect>
                <button
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="p-1 text-muted-foreground hover:text-rose-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <Button
            onClick={() => importMutation.mutate()}
            disabled={files.length === 0 || importMutation.isPending}
          >
            <Upload className="w-4 h-4 mr-2" />
            {importMutation.isPending ? 'Importing...' : 'Import Files'}
          </Button>
          <Button
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
          >
            <Play className="w-4 h-4 mr-2" />
            {runMutation.isPending ? 'Running...' : 'Run Reconciliation'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-muted/50 p-1 rounded-xl w-fit">
        {(['transactions', 'reckon'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'transactions' ? 'Transactions' : 'Reckon Summary'}
          </button>
        ))}
      </div>

      {/* Transactions tab */}
      {activeTab === 'transactions' && (
        <div className="glass-panel rounded-2xl border border-border/50 overflow-hidden">
          {/* Filters */}
          <div className="flex gap-3 p-4 border-b border-border/50 flex-wrap">
            <CompactSelect
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="matched">Matched</option>
              <option value="unmatched">Unmatched</option>
              <option value="discrepancy">Discrepancy</option>
              <option value="dismissed">Dismissed</option>
            </CompactSelect>
            <CompactSelect
              value={filterGateway}
              onChange={(e) => setFilterGateway(e.target.value)}
            >
              <option value="all">All Gateways</option>
              {gateways.map((gw) => (
                <option key={gw} value={gw}>
                  {gw}
                </option>
              ))}
            </CompactSelect>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground bg-muted border-b border-border/50">
                <tr>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Order / Reference</th>
                  <th className="px-5 py-4">Gateway</th>
                  <th className="px-5 py-4">Source</th>
                  <th className="px-5 py-4 text-right">Amount</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Linked</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {txnsLoading ? (
                  [1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="animate-pulse">
                      {Array.from({ length: 8 }).map((_, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-3 bg-muted/50 rounded w-20" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredTxns?.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-muted-foreground">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  filteredTxns?.map((t) => (
                    <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-5 py-3 text-muted-foreground font-mono text-xs">
                        {fmtDate(t.transactionDate)}
                      </td>
                      <td className="px-5 py-3 text-foreground font-mono text-xs max-w-[160px] truncate">
                        {t.orderId || t.reference || t.externalId || '—'}
                      </td>
                      <td className="px-5 py-3 text-foreground text-xs">{t.gateway}</td>
                      <td className="px-5 py-3 text-muted-foreground text-xs">{t.source}</td>
                      <td className="px-5 py-3 text-right font-mono text-sm text-foreground">
                        ${Number(t.amount).toFixed(2)}
                        {t.amountDiff && Number(t.amountDiff) > 0 && (
                          <span className="ml-1 text-xs text-amber-400">
                            (Δ${Number(t.amountDiff).toFixed(2)})
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">{matchStatusBadge(t.matchStatus)}</td>
                      <td className="px-5 py-3 text-xs text-muted-foreground font-mono">
                        {t.linkedTxnId ? (
                          <span className="text-primary">{t.linkedTxnId.slice(0, 8)}…</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          {t.matchStatus === 'unmatched' && (
                            <>
                              <button
                                onClick={() => setLinkTxn(t)}
                                title="Link manually"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                              >
                                <Link2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() =>
                                  patchMutation.mutate({ txnId: t.id, action: 'dismiss' })
                                }
                                title="Dismiss"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                              >
                                <Ban className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          {(t.matchStatus === 'matched' || t.matchStatus === 'discrepancy') && (
                            <button
                              onClick={() =>
                                patchMutation.mutate({ txnId: t.id, action: 'unlink' })
                              }
                              title="Unlink"
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reckon Summary tab */}
      {activeTab === 'reckon' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Configure the Reckon chart-of-account name for each gateway, then use this view while
            entering figures into Reckon Hosted.
          </p>

          {!summary ? (
            <div className="glass-panel rounded-2xl border border-border p-8 text-center text-muted-foreground">
              Run reconciliation first to generate the Reckon summary.
            </div>
          ) : (
            <div className="glass-panel rounded-2xl border border-border/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground bg-muted border-b border-border/50">
                    <tr>
                      <th className="px-6 py-4">Gateway</th>
                      <th className="px-6 py-4">Chart of Account</th>
                      <th className="px-6 py-4 text-right">Opening Balance</th>
                      <th className="px-6 py-4 text-right">Total Received</th>
                      <th className="px-6 py-4 text-right">Fees / Adjustments</th>
                      <th className="px-6 py-4 text-right">Closing Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {Object.entries(summary?.gatewaySummary ?? {}).map(
                      ([gw, data]) => (
                        <tr key={gw} className="hover:bg-muted/30 transition-colors">
                          <td className="px-6 py-4 text-foreground font-medium">{gw}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={getMappedName(gw)}
                                onChange={(e) =>
                                  setEditedMappings((prev) => ({
                                    ...prev,
                                    [gw]: e.target.value,
                                  }))
                                }
                                className="h-8 px-3 bg-muted/50 border border-border rounded-lg text-xs text-foreground focus:outline-none focus:border-primary/50 w-52"
                                placeholder="e.g. 1-1234 Afterpay Clearing"
                              />
                              <button
                                onClick={() => handleSaveMapping(gw)}
                                disabled={savingMapping === gw}
                                className="px-2 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors disabled:opacity-50"
                              >
                                {savingMapping === gw ? '…' : 'Save'}
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right font-mono text-foreground">
                            ${Number(data.openingBalance ?? 0).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-right font-mono text-emerald-400">
                            ${Number(data.totalReceived ?? 0).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-right font-mono text-rose-400">
                            ${Number(data.fees ?? 0).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-right font-mono font-bold text-foreground">
                            ${Number(data.closingBalance ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() =>
                downloadExport(exportReckonCsvUrl(id!), `reckon-summary-${id}.csv`)
              }
              disabled={session.status !== 'done'}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Reckon Summary CSV
            </Button>
            <Button
              onClick={() =>
                downloadExport(exportTransactionsCsvUrl(id!), `reconciliation-${id}.csv`)
              }
              variant="ghost"
              disabled={session.status !== 'done'}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Full Transactions CSV
            </Button>
          </div>
        </div>
      )}

      {/* Manual link modal */}
      {linkTxn && transactions && (
        <ManualLinkModal
          txn={linkTxn}
          allTxns={transactions}
          onLink={(linkedId) =>
            patchMutation.mutate({ txnId: linkTxn.id, action: 'link', linkedTxnId: linkedId })
          }
          onClose={() => setLinkTxn(null)}
        />
      )}
    </Layout>
  );
}
